import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { api } from '../services/api';
import {
  BadgeCheck, ShieldCheck, Upload, FileText, CheckCircle2,
  ArrowRight, AlertCircle,
} from 'lucide-react';

const SECTORS = [
  { id: 'insurance',  label: 'Insurance broker' },
  { id: 'wealth',     label: 'Wealth manager' },
  { id: 'solicitor',  label: 'Solicitor' },
  { id: 'accountant', label: 'Accountant' },
  { id: 'other',      label: 'Other advisor' },
];

export default function BrokerJoin() {
  const { slug } = useParams();
  const [firm, setFirm] = useState(null);
  const [firmLoading, setFirmLoading] = useState(true);
  const [form, setForm] = useState({
    firm_name: '',
    applicant_name: '',
    applicant_email: '',
    phone: '',
    fca_number: '',
    sector: 'insurance',
    notes: '',
  });
  const [proof, setProof] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!slug) { setFirmLoading(false); return; }
    api.getBrokerBySlug(slug)
      .then(r => {
        setFirm(r.data);
        // Prefill firm name + sector to make the form 30-second-fast for the firm itself
        setForm(f => ({
          ...f,
          firm_name: r.data?.firm_name || '',
          sector: r.data?.sector || 'insurance',
        }));
      })
      .catch(() => setFirm(null))
      .finally(() => setFirmLoading(false));
  }, [slug]);

  const handleFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) { setProof(null); return; }
    if (f.size > 8 * 1024 * 1024) {
      setError('Proof file is too large (max 8 MB)');
      return;
    }
    setError(null);
    setProof(f);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    if (!form.firm_name.trim() || !form.applicant_name.trim() || !form.applicant_email.trim()) {
      setError('Firm, name and email are required'); return;
    }
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append('firm_name', form.firm_name.trim());
      fd.append('applicant_name', form.applicant_name.trim());
      fd.append('applicant_email', form.applicant_email.trim());
      if (form.phone) fd.append('phone', form.phone.trim());
      if (form.fca_number) fd.append('fca_number', form.fca_number.trim());
      fd.append('sector', form.sector);
      if (form.notes) fd.append('notes', form.notes.trim());
      if (proof) fd.append('proof', proof);
      await api.submitBrokerApplication(fd);
      setDone(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Could not submit your application');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      {/* Header bar */}
      <header className="border-b border-[#1A1A1A] bg-[#080808]">
        <div className="max-w-5xl mx-auto px-6 lg:px-12 py-5 flex items-center justify-between gap-4 flex-wrap">
          <Link to={firm ? `/brokers/${firm.firm_slug}` : '/'} className="flex items-center gap-3" data-testid="broker-join-back">
            <BadgeCheck size={18} className="text-gold" />
            <span className="font-serif text-lg">Assagart</span>
            {firm && <span className="text-xs text-muted-foreground hidden sm:inline">- partner application for {firm.firm_name}</span>}
          </Link>
          <Link to="/" className="text-xs text-muted-foreground hover:text-gold">Back to Assagart</Link>
        </div>
      </header>

      {done ? (
        <section className="max-w-2xl mx-auto px-6 lg:px-12 py-20 text-center" data-testid="broker-join-success">
          <div className="w-16 h-16 rounded-full bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center mx-auto mb-6">
            <CheckCircle2 size={28} className="text-emerald-400" />
          </div>
          <h1 className="font-serif text-3xl text-white mb-3">Application received</h1>
          <p className="text-sm text-muted-foreground mb-6 leading-relaxed">
            Thank you. We've sent a confirmation to <span className="text-white">{form.applicant_email}</span>.
            Our team will verify your FCA registration within 2 business days and email you your partner
            portal login once approved.
          </p>
          <Link to="/">
            <Button className="bg-gold text-black hover:bg-gold/90" data-testid="broker-join-home">
              Return to Assagart <ArrowRight size={14} className="ml-2" />
            </Button>
          </Link>
        </section>
      ) : (
        <section className="max-w-3xl mx-auto px-6 lg:px-12 py-12">
          <p className="text-gold uppercase tracking-[0.2em] text-xs mb-3">Partner Network</p>
          <h1 className="font-serif text-3xl lg:text-4xl text-white mb-3">
            Join the Assagart broker network
          </h1>
          <p className="text-sm text-muted-foreground mb-8 leading-relaxed max-w-2xl">
            We verify every partner against the FCA's Financial Services Register before granting access.
            Approved partners receive a dedicated inbox, see action tickets and encrypted PII shares from
            their clients in real time, and earn a co-branded landing page for client referrals.
          </p>

          {firmLoading ? null : firm ? (
            <div className="bg-emerald-500/5 border border-emerald-500/30 rounded p-4 mb-8 flex items-start gap-3" data-testid="broker-join-firm">
              <ShieldCheck size={16} className="text-emerald-400 mt-0.5" />
              <div>
                <p className="text-sm text-emerald-300">Applying via the verified <strong>{firm.firm_name}</strong> page.</p>
                <p className="text-xs text-muted-foreground mt-1">
                  We've pre-filled your firm name. If you're not a {firm.firm_name} broker, just edit the field below.
                </p>
              </div>
            </div>
          ) : null}

          <form onSubmit={handleSubmit} className="space-y-5" data-testid="broker-join-form">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-xs text-muted-foreground uppercase tracking-wider">Firm name</Label>
                <Input
                  value={form.firm_name}
                  onChange={(e) => setForm({ ...form, firm_name: e.target.value })}
                  className="luxury-input"
                  placeholder="Aon UK Ltd"
                  required
                  data-testid="join-firm-name"
                />
              </div>
              <div>
                <Label className="text-xs text-muted-foreground uppercase tracking-wider">Sector</Label>
                <select
                  value={form.sector}
                  onChange={(e) => setForm({ ...form, sector: e.target.value })}
                  className="luxury-input w-full bg-[#0A0A0A] border border-[#262626] text-white rounded px-3 py-2 text-sm"
                  data-testid="join-sector"
                >
                  {SECTORS.map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
                </select>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-xs text-muted-foreground uppercase tracking-wider">Your name</Label>
                <Input
                  value={form.applicant_name}
                  onChange={(e) => setForm({ ...form, applicant_name: e.target.value })}
                  className="luxury-input"
                  placeholder="Jane Whitcombe"
                  required
                  data-testid="join-applicant-name"
                />
              </div>
              <div>
                <Label className="text-xs text-muted-foreground uppercase tracking-wider">Work email</Label>
                <Input
                  type="email"
                  value={form.applicant_email}
                  onChange={(e) => setForm({ ...form, applicant_email: e.target.value })}
                  className="luxury-input"
                  placeholder="jane@aon.com"
                  required
                  data-testid="join-applicant-email"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-xs text-muted-foreground uppercase tracking-wider">FCA reference</Label>
                <Input
                  value={form.fca_number}
                  onChange={(e) => setForm({ ...form, fca_number: e.target.value })}
                  className="luxury-input"
                  placeholder="Optional - we'll verify either way"
                  data-testid="join-fca"
                />
              </div>
              <div>
                <Label className="text-xs text-muted-foreground uppercase tracking-wider">Phone</Label>
                <Input
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  className="luxury-input"
                  placeholder="Optional"
                  data-testid="join-phone"
                />
              </div>
            </div>

            <div>
              <Label className="text-xs text-muted-foreground uppercase tracking-wider">A note for our team (optional)</Label>
              <textarea
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                rows={3}
                placeholder="Anything we should know, plus a link to your firm page or LinkedIn"
                className="w-full bg-[#0A0A0A] border border-[#262626] rounded p-3 text-sm text-white leading-relaxed focus:outline-none focus:border-gold/40"
                data-testid="join-notes"
              />
            </div>

            <div className="border border-dashed border-[#262626] rounded p-4 bg-[#080808]">
              <Label className="text-xs text-muted-foreground uppercase tracking-wider flex items-center gap-2">
                <FileText size={12} /> FCA proof (optional, PDF or image, max 8 MB)
              </Label>
              <p className="text-[11px] text-muted-foreground mt-1 mb-3">
                A screenshot or PDF of your FSR entry, or a recent compliance certificate. Speeds up verification.
              </p>
              <label className="inline-flex items-center gap-2 px-3 py-2 bg-[#0A0A0A] border border-[#262626] rounded cursor-pointer hover:border-gold/40 transition-colors">
                <Upload size={14} className="text-gold" />
                <span className="text-xs text-white">{proof ? proof.name : 'Choose file'}</span>
                <input
                  type="file"
                  accept="application/pdf,image/*"
                  onChange={handleFile}
                  className="hidden"
                  data-testid="join-proof-input"
                />
              </label>
            </div>

            {error && (
              <div className="bg-red-500/5 border border-red-500/30 rounded p-3 flex items-start gap-2 text-xs text-red-300" data-testid="join-error">
                <AlertCircle size={14} className="text-red-400 flex-shrink-0 mt-0.5" />
                <span>{error}</span>
              </div>
            )}

            <div className="flex flex-wrap items-center justify-between gap-3 pt-2">
              <p className="text-[11px] text-muted-foreground max-w-md">
                By submitting, you confirm you're authorised to represent your firm. We never share your
                application details with anyone other than our verification team.
              </p>
              <Button
                type="submit"
                disabled={submitting}
                className="bg-gradient-to-r from-gold to-amber-300 text-black font-semibold px-8"
                data-testid="join-submit"
              >
                {submitting ? 'Submitting...' : <>Submit application <ArrowRight size={14} className="ml-2" /></>}
              </Button>
            </div>
          </form>
        </section>
      )}
    </div>
  );
}
